import { useState, useEffect } from "react";
import api from "../services/api";

const T = {
  brand:"#F97316", bg:"#F8FAFC", card:"#FFFFFF",
  bdr:"#E2E8F0", t0:"#0F172A", t1:"#1E293B", t2:"#475569", t3:"#94A3B8",
  green:"#10B981", greenBg:"#ECFDF5", greenBdr:"#6EE7B7",
  red:"#EF4444", redBg:"#FEF2F2", redBdr:"#FCA5A5",
  amber:"#F59E0B", amberBg:"#FFFBEB", amberBdr:"#FCD34D",
  blue:"#3B82F6", blueBg:"#EFF6FF", blueBdr:"#93C5FD",
};

const SEV = {
  critical:{ label:"Critical", color:T.red,   bg:T.redBg,   bdr:T.redBdr,   countColor:"#991B1B" },
  warning: { label:"Warning",  color:T.amber, bg:T.amberBg, bdr:T.amberBdr, countColor:"#92400E" },
  info:    { label:"Info",     color:T.blue,  bg:T.blueBg,  bdr:T.blueBdr,  countColor:"#1E40AF" },
};

function Sparkline({ data, color }) {
  const max = Math.max(...data, 1);
  const W = 60, H = 22, bw = 4, gap = 2;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{flexShrink:0}}>
      {data.map((v, i) => {
        const h = Math.max(2, Math.round((v / max) * (H - 2)));
        return <rect key={i} x={i*(bw+gap)} y={H-h} width={bw} height={h}
          rx="1" fill={color} opacity={v===max?0.9:0.4}/>;
      })}
    </svg>
  );
}

function DonutChart({ critical, warning, info }) {
  const total = critical + warning + info || 0;
  const R=38, cx=50, cy=50, sw=10, circ=2*Math.PI*R;
  const segs=[{v:critical,color:"#E24B4A"},{v:warning,color:"#EF9F27"},{v:info,color:"#378ADD"}];
  let offset=-90;
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
      <div style={{position:"relative",width:100,height:100}}>
        <svg width={100} height={100} viewBox="0 0 100 100" style={{display:"block"}}>
          <circle cx={cx} cy={cy} r={R} fill="none" stroke={T.bdr} strokeWidth={sw}/>
          {total===0
            ? <circle cx={cx} cy={cy} r={R} fill="none" stroke={T.bdr2}
                strokeWidth={sw} strokeDasharray="4 3" opacity={0.6}/>
            : segs.map((s,i)=>{
                if(!s.v) return null;
                const pct=s.v/total, dash=pct*circ, rot=offset;
                offset+=pct*360;
                return <circle key={i} cx={cx} cy={cy} r={R} fill="none"
                  stroke={s.color} strokeWidth={sw}
                  strokeDasharray={`${dash.toFixed(2)} ${(circ-dash).toFixed(2)}`}
                  strokeDashoffset={((1-pct)*circ).toFixed(2)}
                  transform={`rotate(${rot} ${cx} ${cy})`}
                  strokeLinecap="round"/>;
              })
          }
        </svg>
        <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",
          alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
          <span style={{fontSize:24,fontWeight:600,color:T.t0,lineHeight:1}}>{total}</span>
          <span style={{fontSize:9,color:T.t3,marginTop:2}}>total</span>
        </div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:5,width:"100%"}}>
        {[["Critical","#E24B4A",critical],["Warning","#EF9F27",warning],["Info","#378ADD",info]]
          .map(([l,c,v])=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:T.t2}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:c,flexShrink:0}}/>
            <span style={{flex:1}}>{l}</span>
            <span style={{fontWeight:600,color:T.t1}}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarChart({ hourly }) {
  const [hovered, setHovered] = useState(null);
  const data = hourly?.length
    ? hourly
    : [
        {critical:0,warning:1,info:2},{critical:1,warning:2,info:3},
        {critical:0,warning:1,info:2},{critical:2,warning:3,info:4},
        {critical:1,warning:2,info:5},{critical:0,warning:1,info:3},
        {critical:1,warning:4,info:6},{critical:3,warning:5,info:7},
        {critical:2,warning:3,info:4},{critical:1,warning:2,info:3},
        {critical:0,warning:1,info:2},{critical:1,warning:2,info:3},
        {critical:4,warning:6,info:8},{critical:3,warning:5,info:9},
        {critical:2,warning:4,info:6},{critical:1,warning:3,info:5},
        {critical:5,warning:7,info:10},{critical:4,warning:6,info:8},
        {critical:3,warning:4,info:6},{critical:2,warning:3,info:5},
        {critical:1,warning:2,info:4},{critical:0,warning:2,info:3},
        {critical:1,warning:3,info:5},{critical:2,warning:4,info:6},
      ];
  const W=460, H=88, bw=13, gap=3.5;
  const maxV = Math.max(...data.map(d=>(d.critical||0)+(d.warning||0)+(d.info||0)), 1);
  const scale = W/(data.length*(bw+gap));
  const bws = bw*scale;
  return (
    <div>
      <div style={{fontSize:9,color:T.t3,letterSpacing:.5,marginBottom:6}}>Hourly alert volume</div>
      <div style={{position:"relative"}}>
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{display:"block"}}>
          {[0.25,0.5,0.75,1].map(p=>(
            <line key={p} x1={0} y1={H*(1-p)} x2={W} y2={H*(1-p)}
              stroke={T.bdr} strokeWidth={0.5}/>
          ))}
          {data.map((d,i)=>{
            const c=d.critical||0, w=d.warning||0, inf=d.info||0;
            const x=i*(bw+gap)*scale;
            const ih=Math.max(inf>0?2:0,(inf/maxV)*H);
            const wh=Math.max(w>0?2:0,(w/maxV)*H);
            const ch=Math.max(c>0?2:0,(c/maxV)*H);
            const isHov=hovered===i;
            return (
              <g key={i}>
                {ih>0&&<rect x={x} y={H-ih} width={bws} height={ih} rx={2} fill="#378ADD" opacity={isHov?1:0.65}/>}
                {wh>0&&<rect x={x} y={H-ih-wh} width={bws} height={wh} rx={2} fill="#EF9F27" opacity={isHov?1:0.8}/>}
                {ch>0&&<rect x={x} y={H-ih-wh-ch} width={bws} height={ch} rx={2} fill="#E24B4A" opacity={isHov?1:0.9}/>}
                <rect x={x} y={0} width={bws} height={H} fill="transparent" style={{cursor:"crosshair"}}
                  onMouseEnter={()=>setHovered(i)} onMouseLeave={()=>setHovered(null)}/>
              </g>
            );
          })}
        </svg>
        {hovered!==null&&(()=>{
          const d=data[hovered];
          return (
            <div style={{position:"absolute",top:-52,left:`${Math.min(hovered/24*100,75)}%`,
              background:T.t0,color:"white",fontSize:10,padding:"6px 10px",
              borderRadius:7,pointerEvents:"none",zIndex:10,whiteSpace:"nowrap",
              boxShadow:"0 4px 12px rgba(0,0,0,.2)"}}>
              <div style={{fontWeight:600,marginBottom:3,color:T.t4,fontSize:9}}>
                {String(hovered).padStart(2,"0")}:00</div>
              <div style={{display:"flex",gap:8}}>
                <span style={{color:"#F09595"}}>● {d.critical||0} crit</span>
                <span style={{color:"#FAC775"}}>● {d.warning||0} warn</span>
                <span style={{color:"#85B7EB"}}>● {d.info||0} info</span>
              </div>
            </div>
          );
        })()}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
        {["00:00","06:00","12:00","18:00","24:00"].map(l=>(
          <span key={l} style={{fontSize:9,color:T.t3}}>{l}</span>
        ))}
      </div>
      <div style={{display:"flex",gap:14,marginTop:10}}>
        {[["Critical","#E24B4A"],["Warning","#EF9F27"],["Info","#378ADD"]].map(([l,c])=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:T.t2}}>
            <div style={{width:20,height:4,borderRadius:2,background:c,opacity:.85}}/>
            {l}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AlertsDashboard() {
  const [summary, setSummary] = useState({ hourly:[] });
  const [alerts,  setAlerts]  = useState([]);
  const [search,  setSearch]  = useState("");
  const [filter,  setFilter]  = useState("all");
  const [toggles, setToggles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    Promise.all([
      api.get("/api/alerts/summary"),
      api.get("/api/alerts/list"),
    ]).then(([s,l])=>{
      setSummary(s.data);
      setAlerts(l.data.alerts);
      const t={};
      l.data.alerts.forEach(a=>{ t[a.id]=a.enabled; });
      setToggles(t);
    }).catch(()=>{
      // fallback static data while backend isn't ready
      setAlerts([
        {id:"high_violations",name:"High violation sessions",desc:"Candidates require review before any hiring decision",last:"Just now",campaign:"All positions",count:0,severity:"critical",enabled:true,sparkline:[1,2,0,3,1,0,2,1,0,0]},
        {id:"invites_not_sent",name:"Invites not sent",desc:"Candidates added but interview link not delivered yet",last:"2 min ago",campaign:"All positions",count:5,severity:"warning",enabled:true,sparkline:[2,4,3,5,6,4,3,2,5,4]},
        {id:"abandoned_sessions",name:"Abandoned sessions",desc:"Interview started but candidate did not complete it",last:"14 min ago",campaign:"Backend Q1",count:3,severity:"warning",enabled:true,sparkline:[1,1,2,3,2,1,3,2,1,2]},
        {id:"reports_not_reviewed",name:"Reports not reviewed",desc:"Completed interviews still awaiting recruiter action",last:"1 hr ago",campaign:"All positions",count:45,severity:"info",enabled:true,sparkline:[8,12,9,15,10,8,12,9,11,10]},
        {id:"strong_hires_not_actioned",name:"Strong hires not actioned",desc:"Top-scoring candidates waiting for your next step",last:"3 hr ago",campaign:"Senior Dev Q2",count:0,severity:"info",enabled:false,sparkline:[0,0,1,0,0,1,0,0,0,0]},
      ]);
    }).finally(()=>setLoading(false));
  },[]);

  const totals = summary.hourly.reduce(
    (acc,h)=>({critical:acc.critical+h.critical, warning:acc.warning+h.warning, info:acc.info+h.info}),
    {critical:0,warning:0,info:0}
  );

  const filtered = alerts.filter(a=>{
    const matchF = filter==="all" || a.severity===filter;
    const matchQ = !search || a.name.toLowerCase().includes(search.toLowerCase()) || a.campaign.toLowerCase().includes(search.toLowerCase());
    return matchF && matchQ;
  });

  if(loading) return <div style={{padding:32,textAlign:"center",color:T.t3,fontSize:13}}>Loading alerts...</div>;

  const card = {background:T.card, border:"1px solid "+T.bdr, borderRadius:14, padding:"20px"};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:15,fontWeight:600,color:T.t0}}>Manage alerts</span>
        <button style={{fontSize:11,fontWeight:600,padding:"6px 16px",borderRadius:20,
          background:T.brand+"15",color:T.brand,border:"1px solid "+T.brand+"40",
          cursor:"pointer",fontFamily:"inherit"}}>
          + New alert
        </button>
      </div>

      {/* Summary panel */}
      <div style={card}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:T.t0,marginBottom:2}}>Alert summary</div>
            <div style={{fontSize:11,color:T.t3}}>Past 24 hours · auto-refreshes every 5 min</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,fontWeight:600,
            padding:"3px 10px",borderRadius:20,background:T.greenBg,
            color:T.green,border:`1px solid ${T.greenBdr}`}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:T.green,
              animation:"ping 1.8s ease-in-out infinite"}}/>
            Live
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 120px 200px",gap:24,alignItems:"start"}}>
          <BarChart hourly={summary.hourly}/>
          <DonutChart {...totals}/>
          <div>
            <div style={{fontSize:9,color:T.t3,textTransform:"uppercase",
              letterSpacing:.8,marginBottom:8}}>Highest activity</div>
            {[...alerts].sort((a,b)=>b.count-a.count).slice(0,5).map((a,i)=>{
              const s=SEV[a.severity]||SEV.info;
              return (
                <div key={a.id} style={{display:"flex",alignItems:"center",gap:8,
                  padding:"7px 10px",borderRadius:8,marginBottom:2,cursor:"pointer",
                  transition:"background .12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.bg}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  <div style={{width:16,height:16,borderRadius:"50%",flexShrink:0,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:8,fontWeight:600,background:s.bg,color:s.countColor}}>
                    {i+1}
                  </div>
                  <span style={{fontSize:11,color:T.t2,flex:1,overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.name}</span>
                  <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",
                    borderRadius:20,background:s.bg,color:s.countColor,
                    border:`1px solid ${s.color}40`}}>{a.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search + filters */}
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search alerts..."
          style={{flex:1,fontSize:12,padding:"7px 12px",border:"1px solid "+T.bdr,
            borderRadius:9,background:T.bg,color:T.t1,outline:"none",fontFamily:"inherit"}}
          onFocus={e=>e.target.style.borderColor=T.brand}
          onBlur={e=>e.target.style.borderColor=T.bdr}/>
        {["all","critical","warning","info"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            style={{fontSize:11,fontWeight:filter===f?600:400,
              padding:"6px 14px",borderRadius:20,cursor:"pointer",fontFamily:"inherit",
              transition:"all .15s",textTransform:"capitalize",
              background:filter===f?T.brand+"12":"transparent",
              color:filter===f?T.brand:T.t2,
              border:"1px solid "+(filter===f?T.brand+"50":T.bdr)}}>
            {f}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{...card,padding:0,overflow:"hidden"}}>
        {/* Table header */}
        <div style={{display:"grid",gridTemplateColumns:"2fr 110px 120px 60px 180px 60px",
          gap:8,padding:"9px 18px",borderBottom:"1px solid "+T.bdr,background:T.bg}}>
          {["Alert name","Last triggered","Position","Count","Severity","Status"].map(h=>(
            <span key={h} style={{fontSize:9,fontWeight:700,color:T.t3,
              textTransform:"uppercase",letterSpacing:1}}>{h}</span>
          ))}
        </div>

        {filtered.length===0 ? (
          <div style={{padding:"40px",textAlign:"center",fontSize:12,color:T.t3}}>
            No alerts match your filter.
          </div>
        ) : filtered.map((a,idx)=>{
          const s=SEV[a.severity]||SEV.info;
          const isOn=toggles[a.id]??a.enabled;
          const countColor=a.count===0?"#166534":s.countColor;
          return (
            <div key={a.id} style={{display:"grid",
              gridTemplateColumns:"2fr 110px 120px 60px 180px 60px",
              gap:8,padding:"12px 18px",
              borderBottom:idx<filtered.length-1?"1px solid "+T.bdr:"none",
              alignItems:"center",cursor:"pointer",transition:"background .12s"}}
              onMouseEnter={e=>e.currentTarget.style.background=T.bg}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              <div>
                <div style={{fontSize:12,fontWeight:600,color:T.t0,marginBottom:2}}>{a.name}</div>
                <div style={{fontSize:10,color:T.t3}}>{a.desc}</div>
              </div>
              <span style={{fontSize:11,color:T.t2}}>{a.last}</span>
              <span style={{fontSize:11,color:T.t2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.campaign}</span>
              <span style={{fontSize:16,fontWeight:700,color:countColor}}>{a.count}</span>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Sparkline data={a.sparkline||[1,1,1,1,1,1,1,1,1,1]} color={s.color}/>
                <span style={{fontSize:9,fontWeight:600,padding:"3px 8px",borderRadius:20,
                  background:s.bg,color:s.countColor,border:"1px solid "+s.bdr,
                  whiteSpace:"nowrap"}}>{s.label}</span>
              </div>
              <div>
                <div onClick={()=>setToggles(t=>({...t,[a.id]:!t[a.id]}))}
                  style={{width:36,height:20,borderRadius:10,position:"relative",
                    cursor:"pointer",transition:"background .2s",
                    background:isOn?T.green:T.bdr}}>
                  <div style={{position:"absolute",top:2,
                    left:isOn?16:2,width:16,height:16,borderRadius:"50%",
                    background:"white",transition:"left .2s",
                    boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
